"use strict";

Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.importPublic = exports.privateToPublic = exports.privateToAddress = exports.publicToAddress = exports.pubToAddress = exports.isValidPublic = exports.isValidPrivate = exports.isPrecompiled = exports.generateAddress2 = exports.generateAddress = exports.isValidChecksumAddress = exports.toChecksumAddress = exports.isZeroAddress = exports.isValidAddress = exports.zeroAddress = void 0;

var assert = require('assert');

var ethjsUtil = require('ethjs-util');

var secp256k1 = require('./secp256k1v3-adapter');

var BN = require("bn.js");

var bytes_1 = require("./bytes");

var hash_1 = require("./hash");
/**
 * Returns a zero address.
 */


exports.zeroAddress = function () {
  var addressLength = 20;
  var addr = bytes_1.zeros(addressLength);
  return bytes_1.bufferToHex(addr);
};
/**
 * Checks if the address is a valid. Accepts checksummed addresses too.
 */


exports.isValidAddress = function (address) {
  return /^0x[0-9a-fA-F]{40}$/.test(address);
};
/**
 * Checks if a given address is a zero address.
 */


exports.isZeroAddress = function (address) {
  var zeroAddr = exports.zeroAddress();
  return zeroAddr === bytes_1.addHexPrefix(address);
};
/**
 * Returns a checksummed address.
 *
 * If a eip1191ChainId is provided, the chainId will be included in the checksum calculation. This
 * has the effect of checksummed addresses for one chain having invalid checksums for others.
 * For more details, consult EIP-1191.
 *
 * WARNING: Checksums with and without the chainId will differ. As of 2019-06-26, the most commonly
 * used variation in Ethereum was without the chainId. This may change in the future.
 */


exports.toChecksumAddress = function (address, eip1191ChainId) {
  address = ethjsUtil.stripHexPrefix(address).toLowerCase();
  var prefix = eip1191ChainId !== undefined ? eip1191ChainId.toString() + '0x' : '';
  var hash = hash_1.keccak(prefix + address).toString('hex');
  var ret = '0x';

  for (var i = 0; i < address.length; i++) {
    if (parseInt(hash[i], 16) >= 8) {
      ret += address[i].toUpperCase();
    } else {
      ret += address[i];
    }
  }

  return ret;
};
/**
 * Checks if the address is a valid checksummed address.
 *
 * See toChecksumAddress' documentation for details about the eip1191ChainId parameter.
 */


exports.isValidChecksumAddress = function (address, eip1191ChainId) {
  return exports.isValidAddress(address) && exports.toChecksumAddress(address, eip1191ChainId) === address;
};
/**
 * Generates an address of a newly created contract.
 * @param from The address which is creating this new address
 * @param nonce The nonce of the from account
 */


exports.generateAddress = function (from, nonce) {
  from = bytes_1.toBuffer(from);
  var nonceBN = new BN(nonce);

  if (nonceBN.isZero()) {
    // in RLP we want to encode null in the case of zero nonce
    // read the RLP documentation for an answer if you dare
    return hash_1.rlphash([from, null]).slice(-20);
  } // Only take the lower 160bits of the hash


  return hash_1.rlphash([from, Buffer.from(nonceBN.toArray())]).slice(-20);
};
/**
 * Generates an address for a contract created using CREATE2.
 * @param from The address which is creating this new address
 * @param salt A salt
 * @param initCode The init code of the contract being created
 */


exports.generateAddress2 = function (from, salt, initCode) {
  var fromBuf = bytes_1.toBuffer(from);
  var saltBuf = bytes_1.toBuffer(salt);
  var initCodeBuf = bytes_1.toBuffer(initCode);
  assert(fromBuf.length === 20);
  assert(saltBuf.length === 32);
  var address = hash_1.keccak256(Buffer.concat([Buffer.from('ff', 'hex'), fromBuf, saltBuf, hash_1.keccak256(initCodeBuf)]));
  return address.slice(-20);
};
/**
 * Returns true if the supplied address belongs to a precompiled account (Byzantium).
 */


exports.isPrecompiled = function (address) {
  var a = bytes_1.unpad(address);
  return a.length === 1 && a[0] >= 1 && a[0] <= 8;
};
/**
 * Checks if the private key satisfies the rules of the curve secp256k1.
 */


exports.isValidPrivate = function (privateKey) {
  return secp256k1.privateKeyVerify(privateKey);
};
/**
 * Checks if the public key satisfies the rules of the curve secp256k1
 * and the requirements of Ethereum.
 * @param publicKey The two points of an uncompressed key, unless sanitize is enabled
 * @param sanitize Accept public keys in other formats
 */


exports.isValidPublic = function (publicKey, sanitize) {
  if (sanitize === void 0) {
    sanitize = false;
  }

  if (publicKey.length === 64) {
    // Convert to SEC1 for secp256k1
    return secp256k1.publicKeyVerify(Buffer.concat([Buffer.from([4]), publicKey]));
  }

  if (!sanitize) {
    return false;
  }

  return secp256k1.publicKeyVerify(publicKey);
};
/**
 * Returns the ethereum address of a given public key.
 * Accepts "Ethereum public keys" and SEC1 encoded keys.
 * @param pubKey The two points of an uncompressed key, unless sanitize is enabled
 * @param sanitize Accept public keys in other formats
 */


exports.pubToAddress = function (pubKey, sanitize) {
  if (sanitize === void 0) {
    sanitize = false;
  }

  pubKey = bytes_1.toBuffer(pubKey);

  if (sanitize && pubKey.length !== 64) {
    pubKey = secp256k1.publicKeyConvert(pubKey, false).slice(1);
  }

  assert(pubKey.length === 64); // Only take the lower 160bits of the hash

  return hash_1.keccak(pubKey).slice(-20);
};

exports.publicToAddress = exports.pubToAddress;
/**
 * Returns the ethereum address of a given private key.
 * @param privateKey A private key must be 256 bits wide
 */

exports.privateToAddress = function (privateKey) {
  return exports.publicToAddress(exports.privateToPublic(privateKey));
};
/**
 * Returns the ethereum public key of a given private key.
 * @param privateKey A private key must be 256 bits wide
 */


exports.privateToPublic = function (privateKey) {
  privateKey = bytes_1.toBuffer(privateKey); // skip the type flag and use the X, Y points

  return secp256k1.publicKeyCreate(privateKey, false).slice(1);
};
/**
 * Converts a public key to the Ethereum format.
 */


exports.importPublic = function (publicKey) {
  publicKey = bytes_1.toBuffer(publicKey);

  if (publicKey.length !== 64) {
    publicKey = secp256k1.publicKeyConvert(publicKey, false).slice(1);
  }

  return publicKey;
};